import React, { useState, useEffect } from 'react';
import { Users, Mail, Calendar, UserCheck, Building } from 'lucide-react';
import { apiUrl } from '../config/api';

interface TeamMember {
  id: string;
  email: string;
  fullName: string;
  role: string;
  userType: string;
  joinedAt: string;
  invitedBy: string;
}

interface TeamMembersData {
  teamMembers: TeamMember[];
  company: string;
  totalMembers: number;
}

interface TeamMembersProps {
  user: any;
}

export function TeamMembers({ user }: TeamMembersProps) {
  const [teamData, setTeamData] = useState<TeamMembersData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchTeamMembers = async () => {
      try {
        const token = localStorage.getItem('authToken');
        if (!token) {
          setError('Authentication required');
          setLoading(false);
          return;
        }

        const response = await fetch(apiUrl('/api/team-members'), {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          // Handle 404 specifically
          if (response.status === 404) {
            throw new Error('Team members endpoint not available. Please ensure you are using the correct API configuration.');
          }
          const errorData = await response.json().catch(() => ({ message: 'Failed to fetch team members' }));
          throw new Error(errorData.message || `Failed to fetch team members (${response.status})`);
        }

        const data = await response.json();
        if (data.success) {
          setTeamData(data.data);
          setError(null);
        } else {
          setError(data.message || 'Failed to load team members');
        }
      } catch (error) {
        console.error('Error fetching team members:', error);
        setError(error instanceof Error ? error.message : 'Failed to load team members');
      } finally {
        setLoading(false);
      }
    };

    fetchTeamMembers();
  }, []);

  if (loading) {
    return (
      <div style={{
        background: 'white',
        borderRadius: '12px',
        padding: '2rem',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
        border: '1px solid #e9ecef'
      }}>
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <div style={{
            width: '2rem',
            height: '2rem',
            border: '3px solid #f3f3f3',
            borderTop: '3px solid var(--orange)',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 1rem'
          }} />
          <p style={{ color: 'var(--gray)', margin: 0 }}>Loading team members...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        background: 'white',
        borderRadius: '12px',
        padding: '2rem',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
        border: '1px solid #e9ecef',
        textAlign: 'center'
      }}>
        <p style={{ color: 'var(--gray)', margin: 0 }}>{error}</p>
      </div>
    );
  }

  if (!teamData) {
    return null;
  }

  return (
    <div style={{
      background: 'white',
      borderRadius: '12px',
      padding: '2rem',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
      border: '1px solid #e9ecef'
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '1rem',
        marginBottom: '2rem',
        paddingBottom: '1rem',
        borderBottom: '1px solid #e9ecef'
      }}>
        <div style={{
          width: '3rem',
          height: '3rem',
          background: 'var(--orange)',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <Users style={{ width: '1.5rem', height: '1.5rem', color: 'white' }} />
        </div>
        <div>
          <h2 style={{
            fontSize: '1.5rem',
            fontWeight: '600',
            color: 'var(--dark)',
            margin: '0 0 0.25rem 0',
            fontFamily: 'var(--montserrat)'
          }}>
            Team Members
          </h2>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            color: 'var(--gray)',
            fontSize: '0.9rem'
          }}>
            <Building style={{ width: '1rem', height: '1rem' }} />
            <span>{teamData.company}</span>
            <span>•</span>
            <span>{teamData.totalMembers} members</span>
          </div>
        </div>
      </div>

      {/* Team Members List */}
      {teamData.teamMembers.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '2rem',
          color: 'var(--gray)'
        }}>
          <Users style={{ width: '3rem', height: '3rem', margin: '0 auto 1rem', opacity: 0.5 }} />
          <p style={{ margin: 0, fontSize: '1rem' }}>
            You're the only team member so far.
          </p>
          <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.9rem', opacity: 0.8 }}>
            Invite colleagues to see them here!
          </p>
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gap: '1rem'
        }}>
          {teamData.teamMembers.map((member) => (
            <div
              key={member.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
                padding: '1rem',
                background: '#f8f9fa',
                borderRadius: '8px',
                border: '1px solid #e9ecef',
                transition: 'all 0.2s ease'
              }}
            >
              {/* Avatar */}
              <div style={{
                width: '2.5rem',
                height: '2.5rem',
                background: 'var(--orange)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontWeight: '600',
                fontSize: '0.9rem',
                flexShrink: 0
              }}>
                {member.fullName ? member.fullName.charAt(0).toUpperCase() : member.email.charAt(0).toUpperCase()}
              </div>

              {/* Member Info */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  marginBottom: '0.25rem'
                }}>
                  <h3 style={{
                    fontSize: '1rem',
                    fontWeight: '600',
                    color: 'var(--dark)',
                    margin: 0,
                    fontFamily: 'var(--montserrat)'
                  }}>
                    {member.fullName || member.email}
                  </h3>
                </div>
                
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1rem',
                  color: 'var(--gray)',
                  fontSize: '0.85rem'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                    <Mail style={{ width: '0.875rem', height: '0.875rem' }} />
                    <span>{member.email}</span>
                  </div>
                  
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                    <Calendar style={{ width: '0.875rem', height: '0.875rem' }} />
                    <span>Joined {new Date(member.joinedAt).toLocaleDateString()}</span>
                  </div>
                  
                  {member.invitedBy && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                      <UserCheck style={{ width: '0.875rem', height: '0.875rem' }} />
                      <span>Invited by {member.invitedBy}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
